import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Checkbox } from '../../../components/ui/checkbox';
import { Loader2 } from 'lucide-react';
import { WEEKDAY_NAMES, todayStr } from '../../../lib/scheduleFormat';

const SERVES_OPTIONS = [
  { value: 'next_day', label: 'Next day' },
  { value: 'same_day', label: 'Same day' },
  { value: 'both', label: 'Both days' },
];

// This bakery bakes the night before, so a day serves the NEXT day's
// deliveries unless it is deliberately changed.
const blankDays = () =>
  Array.from({ length: 7 }, (_, weekday) => ({
    weekday,
    closed: true,
    start_time: '05:00',
    end_time: '13:00',
    serves: 'next_day',
  }));

// The usual week, once. Everything else about the pattern is a consequence of
// this table plus how far ahead it should keep the hours booked.
const HoursPatternDialog = ({ open, kitchen, pattern, busy, onClose, onSubmit }) => {
  const [days, setDays] = useState(blankDays());
  const [startDate, setStartDate] = useState('');
  const [horizon, setHorizon] = useState(4);

  useEffect(() => {
    if (!open) return;
    setDays(pattern?.days?.length
      ? pattern.days.map((d) => ({
        weekday: d.weekday,
        closed: Boolean(d.closed),
        start_time: d.start_time || '05:00',
        end_time: d.end_time || '13:00',
        // A closed day's `serves` means nothing, so opening one starts from
        // the next-day default rather than whatever was saved years ago.
        serves: d.closed ? 'next_day' : (d.serves || 'next_day'),
      }))
      : blankDays());
    setStartDate(pattern?.start_date || kitchen?.active_from || todayStr());
    setHorizon(pattern?.horizon_weeks ?? 4);
  }, [open, pattern, kitchen]);

  const setDay = (weekday, patch) =>
    setDays((prev) => prev.map((d) => (d.weekday === weekday ? { ...d, ...patch } : d)));

  const openDays = days.filter((d) => !d.closed);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="hours-pattern-dialog">
        <DialogHeader>
          <DialogTitle>Standing hours at {kitchen?.name}</DialogTitle>
          <DialogDescription>
            The usual hours for each day. These get booked ahead automatically inside the landlord&apos;s
            agreement — never beyond it — and arrive as <strong>planned</strong>, since an automatic
            booking cannot have been agreed with the landlord.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            {days.map((day) => (
              <div
                key={day.weekday}
                className={`grid grid-cols-1 items-center gap-2 rounded-lg border p-2.5 sm:grid-cols-[9rem_1fr_1fr_1fr] ${
                  day.closed ? 'border-gray-200 bg-gray-50' : 'border-amber-200 bg-amber-50/40'
                }`}
                data-testid={`pattern-day-${day.weekday}`}
              >
                <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-900">
                  <Checkbox
                    checked={!day.closed}
                    onCheckedChange={(checked) => setDay(day.weekday, { closed: !checked })}
                    data-testid={`pattern-day-open-${day.weekday}`}
                  />
                  {WEEKDAY_NAMES[day.weekday]}
                </label>
                {day.closed ? (
                  <p className="text-sm text-gray-500 sm:col-span-3">Closed — nothing booked</p>
                ) : (
                  <>
                    <div>
                      <Label htmlFor={`pattern-start-${day.weekday}`} className="text-xs text-gray-600">From</Label>
                      <Input
                        id={`pattern-start-${day.weekday}`} type="time" value={day.start_time}
                        onChange={(e) => setDay(day.weekday, { start_time: e.target.value })}
                        data-testid={`pattern-start-${day.weekday}`}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`pattern-end-${day.weekday}`} className="text-xs text-gray-600">To</Label>
                      <Input
                        id={`pattern-end-${day.weekday}`} type="time" value={day.end_time}
                        onChange={(e) => setDay(day.weekday, { end_time: e.target.value })}
                        data-testid={`pattern-end-${day.weekday}`}
                      />
                    </div>
                    <div>
                      <Label htmlFor={`pattern-serves-${day.weekday}`} className="text-xs text-gray-600">
                        Serves deliveries
                      </Label>
                      <select
                        id={`pattern-serves-${day.weekday}`}
                        className="mt-1 h-10 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
                        value={day.serves}
                        onChange={(e) => setDay(day.weekday, { serves: e.target.value })}
                        data-testid={`pattern-serves-${day.weekday}`}
                      >
                        {SERVES_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="pattern-start-date">Start booking from</Label>
              <Input id="pattern-start-date" type="date" value={startDate}
                onChange={(e) => setStartDate(e.target.value)} data-testid="pattern-start-date" />
            </div>
            <div>
              <Label htmlFor="pattern-horizon">Keep booked (weeks ahead)</Label>
              <Input
                id="pattern-horizon" type="number" min="1" max="8" value={horizon}
                onChange={(e) => setHorizon(e.target.value)} data-testid="pattern-horizon"
              />
              <p className="mt-1 text-xs text-gray-500">
                Between 1 and 8. Each night the horizon is extended by whatever has fallen off the front.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="pattern-cancel">Cancel</Button>
          <Button
            onClick={() => onSubmit({
              days: days.map((d) => ({
                weekday: d.weekday,
                closed: d.closed,
                start_time: d.start_time,
                end_time: d.end_time,
                serves: d.serves,
              })),
              start_date: startDate,
              horizon_weeks: Number(horizon),
            })}
            disabled={busy || openDays.length === 0 || !startDate}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="pattern-save"
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save standing hours
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default HoursPatternDialog;
